import VehicleCard from '../VehicleCard';
import { homeVehicleToCardProps } from './homeTypes';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import { ownerProfilePath } from '../../navigation/browseDestinations';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import Button from '../ui/Button';
import type { HomeVehicleCard } from './homeTypes';

interface HomeMyGarageProps {
  vehicles: HomeVehicleCard[];
}

function vehiclePath(vehicle: HomeVehicleCard, fallbackUsername: string): string {
  const slug = resolveVehicleNavSlug(vehicle);
  const username = (vehicle.owner_username || fallbackUsername || '').trim();
  if (username && slug) return vehicleProfilePath(username, slug);
  return ownerProfilePath(fallbackUsername);
}

/**
 * Compact garage rail above the signed-in feed.
 * Charcoal photo tiles match the pre-collectible home garage snapshot so
 * rarity foil / orange frame glow does not wash the page canvas.
 */
export const HomeMyGarage: React.FC<HomeMyGarageProps> = ({ vehicles }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.username || '';
  const addPath = ownerProfilePath(username, { addVehicle: true });
  const isEmpty = vehicles.length === 0;

  const trackAdd = (surface: string) => {
    void apiService.trackEvent('home_cta_clicked', {
      metadata: { cta: isEmpty ? 'add_first_vehicle' : 'add_vehicle', surface },
    });
  };

  return (
    <section id="home-garage" className="mb-8 sm:mb-10" aria-labelledby="home-my-garage-heading">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2
            id="home-my-garage-heading"
            className="font-display text-lg sm:text-xl font-semibold tracking-tight text-white"
          >
            My Garage
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            {isEmpty
              ? 'Add a build so specs, photos, and history stay with the car.'
              : `${vehicles.length} build${vehicles.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <Button
          type="button"
          variant={isEmpty ? 'primary' : 'ghost'}
          size="sm"
          className="shrink-0 min-h-[40px]"
          onClick={() => {
            trackAdd('my_garage');
            navigate(addPath);
          }}
        >
          Add a build
        </Button>
      </div>

      {isEmpty ? (
        <div
          className="rounded-lg border border-dashed border-white/10 px-4 py-6"
          data-testid="home-my-garage-empty"
        >
          <p className="text-sm text-zinc-400">
            Your garage is empty. Start with one vehicle and keep its story in one place.
          </p>
        </div>
      ) : (
        <ul
          className="-mx-1 flex gap-3 collector-horizontal-rail pt-1 pb-3 px-1 snap-x snap-mandatory scrollbar-thin list-none m-0"
          data-testid="home-my-garage-rail"
        >
          {vehicles.map((vehicle) => {
            const path = vehiclePath(vehicle, username);
            return (
              <li key={vehicle.id} className="snap-start shrink-0 w-[250px] sm:w-[280px]">
                <VehicleCard
                  vehicle={homeVehicleToCardProps({
                    ...vehicle,
                    owner_username: vehicle.owner_username || username,
                  })}
                  isOwner
                  href={path}
                  onVehicleClick={() => {
                    void apiService.trackEvent('home_cta_clicked', {
                      metadata: {
                        cta: 'open_vehicle',
                        surface: 'my_garage',
                        vehicle_id: vehicle.id,
                      },
                    });
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default HomeMyGarage;
